import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FormSchema, useReset } from '@/lib/form-utils';
import { cn } from '@/lib/tailwind-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';
import { DefaultValues, FieldValues, UseFormReturn, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AirForm, AutosaveOptions } from './forms/auto-save-form';

export interface SubmitProps<FormData extends FieldValues> {
  formData: FormData;
  closeModal: () => void;
  reset: () => void;
}

interface FormModalProps<S extends FormSchema> {
  defaultValues?: DefaultValues<z.infer<S>>;
  onSubmit?: (submitProps: SubmitProps<z.infer<S>>) => unknown;
  onClose?: () => unknown;
  children: (props: { form: UseFormReturn<z.infer<S>> }) => ReactNode;
  formSchema: S;
  trigger: ReactNode;
  modalTitle: string;
  cancelBtnLabel?: string;
  hideCancelButton?: boolean;
  hideConfirmButton?: boolean;
  confirmBtnLabel?: string;
  dialogProps?: ComponentProps<typeof Dialog>;
  dialogContentProps?: ComponentProps<typeof DialogContent>;
  submitButtonProps?:
    | ComponentProps<typeof Button>
    | ((form: UseFormReturn<z.infer<S>>) => ComponentProps<typeof Button>);
  shouldResetOnCancel?: boolean;
  resetOnDefaultValuesChange?: boolean;
  validateOnOpen?: boolean;
  autosaveOptions?: AutosaveOptions<z.infer<S>>;
  initialOpen?: boolean;
  isFullScreen?: boolean;
  preventExternalClose?: boolean;
  footerContent?: (props: { form: UseFormReturn<z.infer<S>> }) => ReactNode;
  footerLeadingContent?: (props: { form: UseFormReturn<z.infer<S>> }) => ReactNode;
}

export function FormModal<S extends FormSchema>({
  children,
  defaultValues,
  onClose,
  onSubmit,
  formSchema,
  trigger,
  cancelBtnLabel,
  hideCancelButton = false,
  hideConfirmButton = false,
  confirmBtnLabel,
  modalTitle,
  dialogProps = {},
  dialogContentProps = {},
  submitButtonProps,
  shouldResetOnCancel = true,
  resetOnDefaultValuesChange = false,
  validateOnOpen = false,
  autosaveOptions,
  initialOpen = false,
  isFullScreen = false,
  preventExternalClose = false,
  footerContent,
  footerLeadingContent,
}: FormModalProps<S>) {
  const [open, setOpen] = useState(initialOpen);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onChange',
  });
  const reset = useReset(form, defaultValues);

  useEffect(() => {
    if (open && validateOnOpen) {
      // validate the form with the default values
      void form.trigger();
    }
  }, [open, form, validateOnOpen]);

  /**
   * React Hook Form only uses defaultValues on the initial mount of the form instance.
   * If defaultValues changes after the form is created (e.g., when editing a different entity),
   * the form state does NOT automatically update to reflect the new defaults.
   * This can cause the form to show stale data even if the parent component receives new props.
   *
   * Specifically, this happens when a modal componet already rendered with data while react-query
   * invalidates and refetches the data. When the new data arrives, the modal prop and defaultValues change,
   * but the form does not reset to the new defaultValues automatically.
   *
   * To address this, we use a useEffect to manually reset the form when defaultValues changes.
   * We compare the previous and current defaultValues using JSON.stringify, which works for most
   * serializable data. This is a temporary workaround; for non-serializable data,
   * a custom deep comparison or serialization may be needed. For now, we use this as a stop-gap for only
   * select modals so we don't need more complex equality checks.
   *
   * Note: This effect is opt-in via the resetOnDefaultValuesChange prop to avoid unnecessary resets.
   */
  const prevDefaultsString = useRef<string>(JSON.stringify(defaultValues));
  useEffect(() => {
    if (!resetOnDefaultValuesChange) return;
    const currDefaultsString = JSON.stringify(defaultValues);
    if (prevDefaultsString.current !== currDefaultsString) {
      form.reset(defaultValues);
      prevDefaultsString.current = currDefaultsString;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, resetOnDefaultValuesChange]);

  const { className: dialogContentClassName, ...otherContentProps } = dialogContentProps;

  const isIntentionalClose = useRef(false);

  // Prevent modal from closing when clicking outside or pressing escape if preventExternalClose is true
  useEffect(() => {
    if (!preventExternalClose || !open) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [preventExternalClose, open]);

  const handleOnClose = () => {
    isIntentionalClose.current = true;
    if (shouldResetOnCancel) {
      reset();
      form.clearErrors();
    }
    onClose?.();
  };

  const resolvedSubmitButtonProps =
    typeof submitButtonProps === 'function' ? submitButtonProps(form) : submitButtonProps ?? {};

  return (
    <>
      <Dialog
        {...dialogProps}
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && preventExternalClose && !isIntentionalClose.current) return;
          isIntentionalClose.current = false;
          setOpen(nextOpen);

          if (!nextOpen) {
            handleOnClose();
          }
        }}
      >
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent
          className={cn(
            'lg:max-w-screen-lg overflow-y-auto max-h-[80%]',
            isFullScreen && 'w-[calc(100dvw-50px)] h-[calc(100dvh-50px)] max-w-none lg:max-w-none max-h-none',
            dialogContentClassName,
          )}
          onCloseModal={handleOnClose}
          onInteractOutside={preventExternalClose ? (e) => e.preventDefault() : undefined}
          {...otherContentProps}
        >
          <AirForm form={form} autosaveOptions={autosaveOptions}>
            <form
              onSubmit={(e) => {
                e.stopPropagation();
                form.handleSubmit(
                  (formData) => {
                    e?.stopPropagation();
                    return onSubmit?.({
                      formData,
                      closeModal: () => setOpen(false),
                      reset,
                    });
                  },
                  (error) => {
                    console.log('error submitting form', error);
                  },
                )(e);
              }}
            >
              <DialogHeader>
                <DialogTitle>{modalTitle}</DialogTitle>
              </DialogHeader>
              <>{children({ form })}</>
              {footerLeadingContent ? (
                <DialogFooter className="gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 flex-1 items-center sm:flex-initial">
                    {footerLeadingContent({ form })}
                  </div>
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:space-x-2">
                    {!hideCancelButton && (
                      <DialogClose asChild>
                        <Button type="button" variant="secondary" onClick={handleOnClose}>
                          {cancelBtnLabel ?? 'Cancel'}
                        </Button>
                      </DialogClose>
                    )}
                    {!hideConfirmButton && (
                      <Button
                        type="submit"
                        loading={form.formState.isSubmitting}
                        disabled={
                          !form.formState.isValid || form.formState.isSubmitting || !!resolvedSubmitButtonProps.disabled
                        }
                        {...resolvedSubmitButtonProps}
                      >
                        {confirmBtnLabel ?? 'Confirm'}
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              ) : (
                <DialogFooter className="justify-end">
                  {!hideCancelButton && (
                    <DialogClose asChild>
                      <Button type="button" variant="secondary" onClick={handleOnClose}>
                        {cancelBtnLabel ?? 'Cancel'}
                      </Button>
                    </DialogClose>
                  )}
                  {footerContent?.({ form })}
                  {!hideConfirmButton && (
                    <Button
                      type="submit"
                      loading={form.formState.isSubmitting}
                      disabled={
                        !form.formState.isValid || form.formState.isSubmitting || !!resolvedSubmitButtonProps.disabled
                      }
                      {...resolvedSubmitButtonProps}
                    >
                      {confirmBtnLabel ?? 'Confirm'}
                    </Button>
                  )}
                </DialogFooter>
              )}
            </form>
          </AirForm>
        </DialogContent>
      </Dialog>
    </>
  );
}
